import { useState, useEffect } from "react";
import Joyride, { CallBackProps, Placement, Step } from "react-joyride";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useTour } from "@/hooks/useTour";
import { ToolTipLayout } from "./ToolTipLayout";
import { useViewportWidth } from "@/hooks/useViewportWidth";

const CreateCircleTour = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { tourState, nextStep, previousStep, skipTour: markTourAsSkipped } = useTour();
  const [run, setRun] = useState(false);
  const isMobile = useViewportWidth() < 768; // Adjust breakpoint as needed

  // Only show tour if it's the create-circle type
  const shouldShowTour = tourState.isActive && tourState.tourType === "create-circle";

  // Get form validation state from tour context
  const isFormValid = tourState.formValidation.isFormValid;

  // Custom next handler for form step
  const handleFormNext = () => {
    if (tourState.currentStep === 2 && location.pathname === "/circle-creation") {
      // Trigger form submission by clicking the submit button
      const submitButton = document.querySelector('button[type="submit"]') as HTMLButtonElement;
      if (submitButton) {
        submitButton.click();
      }
    } else {
      nextStep();
    }
  };

  // Add class to body for welcome step styling
  useEffect(() => {
    const isWelcomeStep = tourState.currentStep === 0 && location.pathname === "/coh";

    if (isWelcomeStep && shouldShowTour) {
      document.body.classList.add("joyride-welcome-step-active");
    } else {
      document.body.classList.remove("joyride-welcome-step-active");
    }

    // Cleanup when component unmounts
    return () => {
      document.body.classList.remove("joyride-welcome-step-active");
    };
  }, [tourState.currentStep, location.pathname, shouldShowTour]);

  // Get responsive placement based on step and device
  const getResponsivePlacement = (
    step: number,
    defaultPlacement: Placement | "center",
  ): Placement | "center" => {
    if (!isMobile) {
      return defaultPlacement;
    }

    // Step 0 (welcome) goes to center on mobile
    if (step === 0) {
      return "center";
    }

    // All other steps go to top on mobile
    return "top";
  };

  // Get responsive styles based on step and device
  const getResponsiveStyles = (step: number) => {
    let maxWidth: string;
    if (isMobile) {
      maxWidth = "100%";
    } else {
      maxWidth = step === 0 ? "556px" : "428px";
    }

    let width: string;
    if (isMobile) {
      width = step === 0 ? "100vw" : "calc(100vw - 32px)";
    } else {
      width = "100%";
    }

    const baseStyles = {
      tooltip: {
        borderRadius: isMobile && step === 0 ? undefined : 16,
        fontSize: isMobile ? 14 : 16,
        fontFamily: "inherit",
        maxWidth,
        width,
        textAlign: "center" as const,
        overflow: "hidden",
      },
      tooltipContent: {
        padding: isMobile ? "24px 16px" : "32px",
      },
    };

    return baseStyles;
  };

  useEffect(() => {
    if (!tourState.isActive) {
      setRun(false);
      return;
    }

    // Reset run state
    setRun(false);

    // Check if this is step 3 (share link step) which needs special handling
    const isShareLinkStep = tourState.currentStep === 3 && location.pathname === "/circle-creation";

    if (isShareLinkStep) {
      // For share link step, wait for the element to appear
      const waitForShareLinkElement = (retryCount = 0) => {
        const maxRetries = 50; // Max 5 seconds
        const targetElement = document.querySelector("#joyride-circle-share-link");

        if (targetElement) {
          setTimeout(() => setRun(true), 150);
        } else if (retryCount < maxRetries) {
          setTimeout(() => waitForShareLinkElement(retryCount + 1), 100);
        } else {
          nextStep();
        }
      };

      // Also set up MutationObserver for immediate detection
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
            const targetElement = document.querySelector("#joyride-circle-share-link");
            if (targetElement && !run) {
              observer.disconnect();
              setTimeout(() => setRun(true), 150);
            }
          }
        });
      });

      observer.observe(document.body, { childList: true, subtree: true });
      waitForShareLinkElement();

      return () => observer.disconnect();
    } else {
      // For all other steps, use simple element detection with minimal delay
      const waitForTargetElement = (retryCount = 0) => {
        const maxRetries = 20; // Max 2 seconds should be enough for regular elements
        const steps = getCurrentPageSteps();
        const stepIndex = getCurrentStepIndex();

        if (steps.length === 0 || stepIndex < 0 || stepIndex >= steps.length) {
          return;
        }

        const currentStep = steps[stepIndex];
        const targetElement = document.querySelector(currentStep.target as string);

        if (targetElement || currentStep.target === "body") {
          setTimeout(() => setRun(true), 50); // Shorter delay for regular elements
        } else if (retryCount < maxRetries) {
          setTimeout(() => waitForTargetElement(retryCount + 1), 100);
        } else {
          nextStep();
        }
      };

      waitForTargetElement();
    }
  }, [tourState.isActive, tourState.currentStep, location.pathname]);

  const getCurrentPageSteps = (): Step[] => {
    const pathname = location.pathname;

    if (pathname === "/coh") {
      return getLandingPageSteps();
    } else if (pathname === "/circle-creation") {
      return getCircleCreationSteps();
    } else if (pathname.startsWith("/circle/")) {
      return getCirclePageSteps();
    }

    return [];
  };

  const skipTour = () => {
    markTourAsSkipped();
    navigate({ to: "/coh" });
  };

  const getLandingPageSteps = (): Step[] => [
    // Step 0: Welcome
    {
      target: "body",
      content: (
        <ToolTipLayout onClose={skipTour} setRun={setRun}>
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "650",
              marginBottom: "16px",
              color: "#232329",
              lineHeight: "1.2",
            }}
          >
            {t("tour.welcome.title")}
          </h2>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "350",
              color: "#232329",
              lineHeight: "1.5",
              margin: "0 0 24px 0",
            }}
          >
            {t("tour.welcome.description")}
          </p>
          <button
            onClick={nextStep}
            className="flex items-center justify-center gap-[10px] rounded-lg bg-primary p-[10px] text-base font-semibold text-[#232329]"
          >
            {t("tour.startTour")}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className={i18n.dir() === "rtl" ? "-scale-x-100" : undefined}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.29289 0.959397C7.68342 0.568873 8.31658 0.568873 8.70711 0.959397L15.7071 7.9594C16.0976 8.34992 16.0976 8.98309 15.7071 9.37361L8.70711 16.3736C8.31658 16.7641 7.68342 16.7641 7.29289 16.3736C6.90237 15.9831 6.90237 15.3499 7.29289 14.9594L12.5858 9.6665H1C0.447715 9.6665 0 9.21879 0 8.6665C0 8.11422 0.447715 7.6665 1 7.6665H12.5858L7.29289 2.37361C6.90237 1.98309 6.90237 1.34992 7.29289 0.959397Z"
                fill="#22222A"
              />
            </svg>
          </button>
        </ToolTipLayout>
      ),
      placement: getResponsivePlacement(0, "center"),
      disableBeacon: true,
      hideFooter: true,
      isFixed: true,
      disableScrolling: true,
      styles: getResponsiveStyles(0),
    },
    // Step 1: Start Circle
    {
      target: "#joyride-start-circle",
      content: (
        <ToolTipLayout
          onClose={skipTour}
          setRun={setRun}
          currentStep={tourState.currentStep}
          totalSteps={tourState.totalSteps}
          onNext={nextStep}
          onPrevious={previousStep}
          showNavigation={true}
          showPrevious={tourState.currentStep > 0}
          showNext={true}
        >
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "650",
              marginBottom: "16px",
              color: "#232329",
              lineHeight: "1.2",
            }}
          >
            {t("tour.startCircle.title")}
          </h2>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "350",
              color: "#232329",
              lineHeight: "1.5",
              margin: "0 0 24px 0",
            }}
          >
            {t("tour.startCircle.description")}
          </p>
        </ToolTipLayout>
      ),
      placement: getResponsivePlacement(1, "left-start") as any,
      hideFooter: true,
      disableBeacon: true,
      styles: getResponsiveStyles(1),
    },
  ];

  const getCircleCreationSteps = (): Step[] => [
    // Step 2: Circle Creation Form
    {
      target: "#joyride-circle-form",
      content: (
        <ToolTipLayout
          onClose={skipTour}
          setRun={setRun}
          currentStep={tourState.currentStep}
          totalSteps={tourState.totalSteps}
          onNext={handleFormNext}
          onPrevious={previousStep}
          showNavigation={true}
          showPrevious={true}
          showNext={isFormValid}
        >
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "650",
              marginBottom: "16px",
              color: "#232329",
              lineHeight: "1.2",
            }}
          >
            {t("tour.circleCreation.title")}
          </h2>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "350",
              color: "#232329",
              lineHeight: "1.5",
              margin: "0 0 24px 0",
            }}
          >
            {t("tour.circleCreation.description")}
          </p>
          {!isFormValid && (
            <p
              style={{
                fontSize: "14px",
                fontWeight: "350",
                color: "#D32F2F",
                lineHeight: "1.4",
                margin: "0 0 16px 0",
                fontStyle: "italic",
              }}
            >
              Please fill in all required fields to continue.
            </p>
          )}
        </ToolTipLayout>
      ),
      placement: getResponsivePlacement(2, "left") as any,
      hideFooter: true,
      disableBeacon: true,
      disableOverlayClose: true,
      disableCloseOnEsc: true,
      spotlightClicks: true, // Allow clicks within the spotlight area
      styles: getResponsiveStyles(2),
    },
    // Step 3: Thank You Step
    {
      target: "#joyride-circle-share-link",
      content: (
        <ToolTipLayout
          onClose={skipTour}
          setRun={setRun}
          currentStep={tourState.currentStep}
          totalSteps={tourState.totalSteps}
          onNext={nextStep}
          onPrevious={previousStep}
          showNavigation={true}
          showPrevious={true}
          showNext={true}
        >
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "650",
              marginBottom: "16px",
              color: "#232329",
              lineHeight: "1.2",
            }}
          >
            {t("tour.shareLink.title")}
          </h2>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "350",
              color: "#232329",
              lineHeight: "1.5",
              margin: "0 0 24px 0",
            }}
          >
            {t("tour.shareLink.description")}
          </p>
        </ToolTipLayout>
      ),
      placement: getResponsivePlacement(3, "bottom") as any,
      hideFooter: true,
      disableBeacon: true,
      disableOverlayClose: true,
      disableCloseOnEsc: true,
      spotlightClicks: true, // Allow clicks within the spotlight area
      styles: getResponsiveStyles(3),
    },
  ];

  const getCirclePageSteps = (): Step[] => [
    // Step 4: Circle Hero
    {
      target: "#joyride-circle-hero",
      content: (
        <ToolTipLayout
          onClose={skipTour}
          setRun={setRun}
          currentStep={tourState.currentStep}
          totalSteps={tourState.totalSteps}
          onNext={nextStep}
          onPrevious={previousStep}
          showNavigation={true}
          showPrevious={true}
          showNext={true}
        >
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "650",
              marginBottom: "16px",
              color: "#232329",
              lineHeight: "1.2",
              paddingInlineEnd: "2.5rem",
            }}
          >
            {t("tour.circleHero.title")}
          </h2>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "350",
              color: "#232329",
              lineHeight: "1.5",
              margin: "0 0 24px 0",
            }}
          >
            {t("tour.circleHero.description")}
          </p>
        </ToolTipLayout>
      ),
      placement: getResponsivePlacement(4, "bottom") as any,
      hideFooter: true,
      disableBeacon: true,
      styles: getResponsiveStyles(4),
    },
    // Step 5: Circle Details
    {
      target: "[data-tour='joyride-circle-details']",
      content: (
        <ToolTipLayout
          onClose={skipTour}
          setRun={setRun}
          currentStep={tourState.currentStep}
          totalSteps={tourState.totalSteps}
          onNext={nextStep}
          onPrevious={previousStep}
          showNavigation={true}
          showPrevious={true}
          showNext={true}
        >
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "650",
              marginBottom: "16px",
              color: "#232329",
              lineHeight: "1.2",
            }}
          >
            {t("tour.circleDetails.title")}
          </h2>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "350",
              color: "#232329",
              lineHeight: "1.5",
              margin: "0 0 24px 0",
            }}
          >
            {t("tour.circleDetails.description")}
          </p>
        </ToolTipLayout>
      ),
      placement: getResponsivePlacement(5, "top") as any,
      hideFooter: true,
      disableBeacon: true,
      disableOverlayClose: true,
      disableCloseOnEsc: true,
      spotlightClicks: true, // Allow clicks within the spotlight area
      styles: getResponsiveStyles(5),
    },
    // Step 6: Circle Members
    {
      target: "[data-tour='joyride-circle-members']",
      content: (
        <ToolTipLayout
          onClose={skipTour}
          setRun={setRun}
          currentStep={tourState.currentStep}
          totalSteps={tourState.totalSteps}
          onNext={nextStep}
          onPrevious={previousStep}
          showNavigation={true}
          showPrevious={true}
          showNext={true}
        >
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "650",
              marginBottom: "16px",
              color: "#232329",
              lineHeight: "1.2",
            }}
          >
            {t("tour.circleMembers.title")}
          </h2>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "350",
              color: "#232329",
              lineHeight: "1.5",
              margin: "0 0 24px 0",
            }}
          >
            {t("tour.circleMembers.description")}
          </p>
        </ToolTipLayout>
      ),
      placement: getResponsivePlacement(6, "top") as any,
      hideFooter: true,
      disableBeacon: true,
      disableOverlayClose: true,
      disableCloseOnEsc: true,
      spotlightClicks: true, // Allow clicks within the spotlight area
      styles: getResponsiveStyles(6),
    },
  ];

  const getCurrentStepIndex = (): number => {
    const pathname = location.pathname;
    let stepIndex = 0;

    if (pathname === "/coh") {
      stepIndex = tourState.currentStep; // Steps 0, 1
    } else if (pathname === "/circle-creation") {
      stepIndex = tourState.currentStep - 2; // Steps 2, 3 -> 0, 1
    } else if (pathname.startsWith("/circle/")) {
      stepIndex = tourState.currentStep - 4; // Steps 4, 5, 6 -> 0, 1, 2
    }
    return stepIndex;
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action } = data;
    // Handle tour completion or skipping
    if (action === "skip" || action === "close") {
      setRun(false);
      markTourAsSkipped(); // Mark as skipped with persistent tracking
      navigate({ to: "/coh" });
    }
  };

  const steps = getCurrentPageSteps();
  const stepIndex = getCurrentStepIndex();

  // More defensive checking - only show for create-circle tour
  if (!shouldShowTour || steps.length === 0 || stepIndex < 0 || stepIndex >= steps.length) {
    return null;
  }

  return (
    <>
      <style>
        {`
          .logo-container svg {
            width: 100% !important;
            height: 100% !important;
            max-width: 134px;
            max-height: 134px;
          }
        `}
      </style>

      <Joyride
        callback={handleJoyrideCallback}
        continuous
        hideCloseButton
        run={shouldShowTour}
        scrollToFirstStep
        showProgress
        showSkipButton={false}
        stepIndex={stepIndex}
        steps={steps}
        styles={{
          options: {
            arrowColor: "#EDEAE4",
            backgroundColor: "#EDEAE4",
            overlayColor: "rgba(0, 0, 0, 0.5)",
            primaryColor: "#232329",
            textColor: "#232329",
            zIndex: 1000,
          },
          tooltip: {
            direction: i18n.dir(),
            borderRadius: 12,
            fontSize: 16,
            fontFamily: "inherit",
          },
          tooltipContainer: {
            textAlign: "start",
          },
        }}
        locale={{
          back: t("tour.back"),
          close: t("tour.close"),
          last: t("tour.finish"),
          next: t("tour.next"),
          skip: t("tour.skip"),
        }}
      />
    </>
  );
};

export default CreateCircleTour;
